import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';

import { concatWithDot } from 'helpers/string';

import { EquipmentItem } from './EquipmentItem';
import { EQUIPMENT_NAMES, equipmentSectionInitialValues, prefix } from './utils/form';

export const EquipmentSection: React.FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.BUSINESS_OPERATIONS,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_BUSINESS_OPERATIONS.EQUIPMENT,
  );

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('businessOperations.title')}
          badgeText={t('businessOperations.equipment.title')}
          section={FOR_BANK_SECTIONS.BUSINESS_OPERATIONS}
        />
      </FormSectionCard>
      {/* 6.2.1 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('businessOperations.equipment.description')}
        aiFieldName={concatWithDot(prefix, EQUIPMENT_NAMES.DESCRIPTION)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, EQUIPMENT_NAMES.DESCRIPTION)}
          name={concatWithDot(prefix, EQUIPMENT_NAMES.DESCRIPTION)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 6.2.2 */}
      <FormSectionCard disabled={!canAccess} title={t('businessOperations.equipment.equipmentGroup.title')} collapsible>
        <FieldArrayComponent
          name={concatWithDot(prefix, EQUIPMENT_NAMES.MAIN_EQUIPMENT)}
          initialItem={equipmentSectionInitialValues}
          buttonText={t('businessOperations.equipment.equipmentGroup.buttonText')}
          disabled={!canAccess}
        >
          {(namePrefix) => <EquipmentItem isCurrentEquipment name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
      {/* 6.2.3 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('businessOperations.equipment.utilizationLevel')}
        aiFieldName={concatWithDot(prefix, EQUIPMENT_NAMES.LEVEL_OF_UTILIZATION)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, EQUIPMENT_NAMES.LEVEL_OF_UTILIZATION)}
          name={concatWithDot(prefix, EQUIPMENT_NAMES.LEVEL_OF_UTILIZATION)}
          minRows={4}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 6.2.4 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('businessOperations.equipment.purchasedEquipment.title')}
        collapsible
      >
        <FieldArrayComponent
          name={concatWithDot(prefix, EQUIPMENT_NAMES.PURCHASED_EQUIPMENT)}
          initialItem={equipmentSectionInitialValues}
          buttonText={t('businessOperations.equipment.purchasedEquipment.buttonText')}
          disabled={!canAccess}
        >
          {(namePrefix) => <EquipmentItem name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
      {/* 6.2.5 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('businessOperations.equipment.rationaleForPurchasing')}
        aiFieldName={concatWithDot(prefix, EQUIPMENT_NAMES.RATIONALE_FOR_PURCHASING)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, EQUIPMENT_NAMES.RATIONALE_FOR_PURCHASING)}
          name={concatWithDot(prefix, EQUIPMENT_NAMES.RATIONALE_FOR_PURCHASING)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
    </>
  );
};
